import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rawFetch } from "@/lib/api/client";
import { DagView, type PipelineDag, type DagStep } from "@/pages/Pipelines";

type ConfigSpec = { type: "integer" | "number"; min: number; max: number };
type PaletteStep = { key: string; label: string; kind: string; config: Record<string, ConfigSpec> };
type SavedPipeline = { id: string; name: string; dag: PipelineDag; current_revision: number };
type SaveResult = { pipeline_id: string; revision: number; digest: string; dag: PipelineDag };
type Preview = SaveResult & { runnable: boolean; deployed: boolean; unavailable_processors: string[] };

const empty: PipelineDag = { steps: [], edges: [] };

function fromRecommendation(processors: string[], palette: PaletteStep[]): PipelineDag {
  const allowed = new Set(palette.map((item) => item.key));
  // Catalog has already parsed and chunked each selected document. Extract
  // review candidates before the identity/normalization stages consume them.
  const keys = ["catalog.document", "enrich.pii_redact", "extract.entities",
    ...processors.filter((key) => ["enrich.iri", "normalize.concept", "enrich.resolve"].includes(key))]
    .filter((key, index, all) => allowed.has(key) && all.indexOf(key) === index);
  const steps = keys.map((processor, index) => ({ id: `step_${index + 1}`, processor, config: {} }));
  return { steps, edges: steps.slice(1).map((step, index) => [steps[index].id, step.id]) };
}

export function StudioDagEditor({ sessionId, sessionName, workspace, processors, canEdit }: {
  sessionId: string; sessionName: string; workspace: string;
  processors: string[]; canEdit: boolean;
}) {
  const qc = useQueryClient();
  const paletteQuery = useQuery<{ steps: PaletteStep[] }>({
    queryKey: ["pipeline-design-palette"],
    queryFn: () => rawFetch("/pipelines/pipelines/design-palette/"),
  });
  const savedQuery = useQuery<{ results: SavedPipeline[] }>({
    queryKey: ["studio-pipeline-design", workspace, sessionId],
    queryFn: () => rawFetch(`/pipelines/pipelines/?workspace=${encodeURIComponent(workspace)}&source_session=${encodeURIComponent(sessionId)}&page_size=1`),
  });
  const palette = paletteQuery.data?.steps ?? [];
  const suggested = useMemo(() => fromRecommendation(processors, palette), [processors, palette]);
  const unsupported = processors.filter((key) => !palette.some((item) => item.key === key));
  const [dag, setDag] = useState<PipelineDag>(empty);
  const [name, setName] = useState(`${sessionName} flow`);
  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const [savedDag, setSavedDag] = useState<PipelineDag | null>(null);
  const [selected, setSelected] = useState<string>("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);

  useEffect(() => {
    setInitialized(false);
    setDag(empty);
    setPipelineId(null);
    setSavedDag(null);
    setPreview(null);
    setName(`${sessionName} flow`);
  }, [sessionId, sessionName]);
  useEffect(() => {
    if (initialized || !paletteQuery.data || !savedQuery.data) return;
    const saved = savedQuery.data.results[0];
    setDag(saved?.dag ?? suggested);
    setSavedDag(saved?.dag ?? null);
    setName(saved?.name ?? `${sessionName} flow`);
    setPipelineId(saved?.id ?? null);
    setRevision(saved?.current_revision ?? 0);
    setInitialized(true);
  }, [initialized, paletteQuery.data, savedQuery.data, suggested, sessionName]);

  const save = useMutation({
    mutationFn: () => rawFetch<SaveResult>("/pipelines/pipelines/save-design/", {
      method: "POST", body: JSON.stringify({
        workspace, name, source_session_id: sessionId, dag,
        ...(pipelineId ? { pipeline_id: pipelineId, expected_revision: revision } : {}),
      }),
    }),
    onSuccess: (result) => {
      setPipelineId(result.pipeline_id);
      setRevision(result.revision);
      setDag(result.dag);
      setSavedDag(result.dag);
      setPreview(null);
      qc.invalidateQueries({ queryKey: ["studio-pipeline-design", workspace, sessionId] });
    },
  });
  const loadPreview = useMutation({
    mutationFn: (id: string) => rawFetch<Preview>(`/pipelines/pipelines/${id}/deploy-preview/`),
    onSuccess: setPreview,
  });
  const steps = dag.steps ?? [];
  const edges: [string, string][] = (dag.edges ?? []).map((edge) =>
    Array.isArray(edge) ? edge : [edge.from, edge.to]);
  const active = steps.find((step) => step.id === selected);
  const activeDefinition = palette.find((item) => item.key === active?.processor);
  const dirty = !savedDag || JSON.stringify(dag) !== JSON.stringify(savedDag);
  const change = (next: PipelineDag) => { setDag(next); setPreview(null); };
  const add = (key: string) => {
    const used = new Set(steps.map((step) => step.id));
    let n = steps.length + 1;
    while (used.has(`step_${n}`)) n += 1;
    const id = `step_${n}`;
    change({ steps: [...steps, { id, processor: key, config: {} }], edges });
    setSelected(id);
  };
  const remove = () => {
    change({ steps: steps.filter((step) => step.id !== selected),
             edges: edges.filter(([a, b]) => a !== selected && b !== selected) });
    setSelected("");
  };
  const updateConfig = (field: string, raw: string) => {
    change({ steps: steps.map((step): DagStep => {
      if (step.id !== selected) return step;
      const config = { ...step.config };
      if (raw === "") delete config[field];
      else config[field] = Number(raw);
      return { ...step, config };
    }), edges });
  };

  return (
    <section className="mt-5 space-y-4 border-t border-border pt-5" aria-label="Pipeline design editor">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h3 className="text-sm font-semibold text-text">Saved pipeline design</h3>
          <p className="text-xs text-muted">This turns the interview into a Catalog document flow: redaction, review candidates, then the supported identity steps. Edit it, save a revision, and inspect exactly what will run.</p></div>
        {pipelineId && <span className="rounded border border-border px-2 py-1 font-mono text-[10px] text-muted">Revision {revision}</span>}
      </div>
      {unsupported.length > 0 && <p className="rounded border border-warn/40 bg-warn/10 p-3 text-xs text-text" role="status">
        Requirements awaiting an installed adapter: {unsupported.join(", ")}. Catalog handles file OCR before this pipeline; live transcription and streaming remain separate integrations. No unsupported step was silently added.
      </p>}
      {(paletteQuery.isError || savedQuery.isError) && <p role="alert" className="text-xs text-bad">Could not load the pipeline editor.</p>}
      <label className="block text-xs text-muted">Pipeline name
        <input className="input mt-1 w-full" value={name} onChange={(event) => setName(event.target.value)} disabled={!canEdit} />
      </label>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_240px]">
        <div className="min-w-0 space-y-3">
          <DagView dag={dag} />
          <div className="flex flex-wrap gap-2" aria-label="Pipeline step palette">
            {palette.map((item) => <button key={item.key} className="btn text-xs" type="button" disabled={!canEdit}
              onClick={() => add(item.key)}>+ {item.label}</button>)}
          </div>
          <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <select className="input" aria-label="Connect from step" value={from} onChange={(event) => setFrom(event.target.value)} disabled={!canEdit}>
              <option value="">From step</option>{steps.map((step) => <option key={step.id} value={step.id}>{step.id}</option>)}
            </select>
            <select className="input" aria-label="Connect to step" value={to} onChange={(event) => setTo(event.target.value)} disabled={!canEdit}>
              <option value="">To step</option>{steps.map((step) => <option key={step.id} value={step.id}>{step.id}</option>)}
            </select>
            <button className="btn" disabled={!canEdit || !from || !to || from === to || edges.some(([a, b]) => a === from && b === to)}
              onClick={() => change({ steps, edges: [...edges, [from, to]] })}>Connect</button>
          </div>
          {edges.length > 0 && <div className="flex flex-wrap gap-2 text-xs text-muted">{edges.map(([a, b]) =>
            <button key={`${a}-${b}`} className="rounded border border-border px-2 py-1 hover:text-text" disabled={!canEdit}
              onClick={() => change({ steps, edges: edges.filter(([x, y]) => x !== a || y !== b) })}
              aria-label={`Remove connection ${a} to ${b}`}>{a} → {b} ×</button>)}</div>}
        </div>
        <aside className="rounded-lg border border-border bg-panel p-3">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Step configuration</h4>
          <label className="block text-xs text-muted">Selected step
            <select className="input mt-1" value={selected} onChange={(event) => setSelected(event.target.value)}>
              <option value="">Choose a step</option>{steps.map((step) => <option key={step.id} value={step.id}>{step.id} · {step.processor}</option>)}
            </select>
          </label>
          {active && <div className="mt-3 space-y-3">
            <p className="text-xs text-text">{activeDefinition?.label ?? active.processor}</p>
            {Object.entries(activeDefinition?.config ?? {}).map(([field, spec]) =>
              <label className="block text-xs text-muted" key={field}>{field.replaceAll("_", " ")}
                <input className="input mt-1" type="number" min={spec.min} max={spec.max}
                  step={spec.type === "integer" ? 1 : 0.01} value={active.config?.[field] == null ? "" : String(active.config[field])}
                  onChange={(event) => updateConfig(field, event.target.value)} disabled={!canEdit} />
              </label>)}
            <button className="btn text-xs text-bad" onClick={remove} disabled={!canEdit}>Remove selected step</button>
          </div>}
        </aside>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn" disabled={!canEdit || !palette.length} onClick={() => change(suggested)}>Restore recommendation</button>
        <button className="btn-primary" disabled={!canEdit || !steps.length || !name.trim() || save.isPending}
          onClick={() => save.mutate()}>{pipelineId ? "Save revision" : "Create pipeline from this blueprint"}</button>
        <button className="btn" disabled={!pipelineId || dirty || loadPreview.isPending}
          onClick={() => pipelineId && loadPreview.mutate(pipelineId)}>Preview saved revision</button>
        {pipelineId && !dirty && <a className="btn" href={`/pipelines?edit=${encodeURIComponent(pipelineId)}`}>
          Open full canvas
        </a>}
      </div>
      {save.isError && <p role="alert" className="text-xs text-bad">Save failed: {String(save.error)}</p>}
      {loadPreview.isError && <p role="alert" className="text-xs text-bad">Preview failed: {String(loadPreview.error)}</p>}
      {preview && <div className="rounded border border-accent/40 bg-accent/5 p-3 text-xs" role="status">
        <strong>Saved revision {preview.revision}</strong> · <code>{preview.digest.slice(0, 16)}…</code> ·
        {preview.runnable ? " ready for a manual run" : ` unavailable: ${preview.unavailable_processors.join(", ")}`}. No deployment was performed.
      </div>}
      {pipelineId && <p className="text-xs text-muted">To run it, choose an accessible Catalog document in Pipelines. Extraction requires a published or pinned ontology and uses only this workspace's LLM key.</p>}
    </section>
  );
}
